/**
 * Centralized pixel geometry for the Trends pipeline.
 * Values reflect the 2026-04-17 visual-tune spec: roomy ~124px lanes,
 * explicit intent→pipeline gap, divider before the Done cell.
 */

export const PIPELINE = {
  width: 1400,
  height: 412,

  // Pipeline region
  pipelineTop: 4,
  pipelineBottom: 380,

  // Input / output columns
  inputX: 0,
  inputWidth: 60,
  inputBarX: 14,
  inputBarWidth: 40,
  inputBarTopY: 44,
  inputBarHeight: 346,

  ribbonLeft: 60,
  ribbonLeftEnd: 160,
  ribbonRight: 1060,
  ribbonRightEnd: 1180,

  outputX: 1180,
  outputWidth: 60,
  outputBarX: 20,
  outputBarWidth: 40,

  // Lanes
  laneX: 160,
  laneWidth: 900,
  laneHeight: 82,
  laneGap: 6,
  laneCount: 4,

  // Within lane — card interior
  lanePaddingTop: 6,
  lanePaddingX: 12,
  laneHeaderY: 12,      // lane label baseline
  laneSubY: 22,         // sub-label baseline
  laneAvatarRowY: 10,   // avatar center y (top-right of header row)

  // Sparkline band — count trend above the cells
  sparklineY: 16,
  sparklineHeight: 14,

  // State cell region — cells are now uniform height; sparkline carries the trend signal
  cellRowTop: 32,
  cellRowBottom: 46,
  cellBaselineY: 46,        // alias — StateCell bottom-aligns here
  cellMinHeight: 14,        // equal to max → uniform
  cellMaxHeight: 14,
  stateLabelY: 54,          // state name row (below cells)

  // Cycle time strip — labeled stacked bar
  cycleTimeStripY: 58,
  cycleTimeStripHeight: 10,
  handoffStripY: 77,        // hand-off labels' baseline, below the cycle strip

  // Cell area (horizontal) — inside the 900-wide lane card
  cellAreaX: 140,
  cellAreaEnd: 780,

  // Done cell — shifted right by the divider + paddings
  doneCellDividerX: 784,
  doneCellGap: 10,
  doneCellX: 792,
  doneCellWidth: 96,
} as const;

export function laneTopY(laneIndex: number): number {
  return PIPELINE.pipelineTop + PIPELINE.inputBarTopY + laneIndex * (PIPELINE.laneHeight + PIPELINE.laneGap);
}

export function cellHeight(count: number, maxCount: number): number {
  if (maxCount <= 0) return PIPELINE.cellMinHeight;
  const ratio = count / maxCount;
  return Math.max(
    PIPELINE.cellMinHeight,
    Math.round(ratio * PIPELINE.cellMaxHeight),
  );
}

/** Layout N non-terminal cells within the cell area with equal gaps. */
export function layoutCells(nCells: number): Array<{ x: number; width: number }> {
  const area = PIPELINE.cellAreaEnd - PIPELINE.cellAreaX;
  if (nCells <= 0) return [];
  const gapMin = 8;
  const cellWidth = Math.floor((area - (nCells - 1) * gapMin) / nCells);
  return Array.from({ length: nCells }, (_, i) => ({
    x: PIPELINE.cellAreaX + i * (cellWidth + gapMin),
    width: cellWidth,
  }));
}
